import { CountryCode, type ProjectCode } from '@constants'
import { type FastpassOrderInvoiceReceiptType } from '@hooks-api/types/dataModel/fpOrder'
import { Box } from '@pets-ui/Box/Box'
import { Checkbox } from '@pets-ui/Checkbox/Checkbox'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useTranslation } from 'react-i18next'

const styles = stylex.create({
  info: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
  },
})

interface InvoiceInfoProps {
  invoiceNumber?: string
  receiptType?: FastpassOrderInvoiceReceiptType
  businessUbnTitle?: string
  businessUbn?: string
  phoneCarrierNumber?: string | null
  charityOrganization?: string | null
  isRefundStatement?: boolean
  rootStyle?: stylex.StyleXStyles
  isInOrderDetailPage?: boolean
  countryCode?: CountryCode
  projectCode?: ProjectCode
}

export const InvoiceInfo = ({ invoiceNumber, receiptType, businessUbnTitle, businessUbn, phoneCarrierNumber, charityOrganization, isRefundStatement, rootStyle, isInOrderDetailPage, countryCode, projectCode }: InvoiceInfoProps) => {
  const { t } = useTranslation()
  const shouldDisplayInvoice = countryCode === CountryCode.TW
  const businessEinvoice = receiptType === 'business_einvoice'
  const nonBusinessEinvoice = receiptType === 'non_business_einvoice'
  const phoneCarrier = receiptType === 'phone_carrier'
  const donateInvoice = receiptType === 'donate_invoice'

  if (!shouldDisplayInvoice) return null
  return (
    <VStack rootStyle={rootStyle}>
      <Text variant="subtitle">{t('orderDetail.invoiceInfo', { defaultValue: '發票資訊' })}</Text>
      {
        isInOrderDetailPage
        && (
          <>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.invoiceNumber', { defaultValue: '發票號碼' })}</Text>
              <Text color={invoiceNumber ? colors.grey600 : colors.grey400}>{invoiceNumber || t('orderDetail.invoiceNotIssued', { defaultValue: '尚未開立' })}</Text>
            </Box>
          </>
        )
      }
      {
        businessEinvoice
        && (
          <>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.invoiceType', { defaultValue: '發票種類' })}</Text>
              <Text>{t('orderDetail.businessEInvoice', { defaultValue: '營業用電子發票' })}</Text>
            </Box>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.vatNumber', { defaultValue: '統一編號' })}</Text>
              <Text>{businessUbn}</Text>
            </Box>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.companyTitle', { defaultValue: '營業人抬頭' })}</Text>
              <Text>{businessUbnTitle}</Text>
            </Box>
          </>
        )
      }
      {
        nonBusinessEinvoice
        && (
          <>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.invoiceType', { defaultValue: '發票種類' })}</Text>
              <Text>{t('orderDetail.personalEInvoice', { defaultValue: '非營業用電子發票' })}</Text>
            </Box>
          </>
        )
      }
      {
        phoneCarrier
        && (
          <>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>
                {t('orderDetail.invoiceType', { defaultValue: '發票種類' })}
                {' '}
              </Text>
              <Text>{t('orderDetail.mobileBarcode', { defaultValue: '手機條碼' })}</Text>
            </Box>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.barcode', { defaultValue: '條碼' })}</Text>
              <Text>{phoneCarrierNumber}</Text>
            </Box>
          </>
        )
      }
      {
        donateInvoice
        && (
          <>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.invoiceType', { defaultValue: '發票種類' })}</Text>
              <Text>{t('orderDetail.donateInvoice', { defaultValue: '捐贈發票' })}</Text>
            </Box>
            <Box rootStyle={styles.info}>
              <Text color={colors.grey600}>{t('orderDetail.donationOrganization', { defaultValue: '捐贈團體' })}</Text>
              <Text>{charityOrganization}</Text>
            </Box>
          </>
        )
      }
      {
        isInOrderDetailPage && isRefundStatement
        && (
          <Box mt={2}>
            <Checkbox
              checked={isRefundStatement}
              disabled
              color="grey"
              label={t(`invoiceStatement.${projectCode?.toLowerCase()}`, { defaultValue: '本人同意退換貨或訂單金額變動時，發票將由自力耕生股份有限公司全權處理作廢或折讓。' })}
              data-testid="checkbox-invoice-statement"
            />
          </Box>
        )
      }
    </VStack>
  )
}
